import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Pagination } from '../components/Pagination';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import CompanyLogo from '@/components/ui/CompanyLogo';
import { ClickToCall } from '@/components/ClickToCall';
import { Linkedin } from 'lucide-react';

// Info icon component
const InfoIcon = () => (
  <svg className="w-3.5 h-3.5 text-slate-400 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Clean KPI Card Component - matching dashboard exactly
const KPICard = ({ title, value, trend, subValue }) => (
  <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:shadow-sm transition-shadow">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
        {title}
        <InfoIcon />
      </span>
      <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
    </div>
    <div className="flex items-baseline gap-2">
      <span className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</span>
      {trend !== undefined && (
        <span className="inline-flex items-center text-xs font-medium text-green-600 dark:text-green-400">
          <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          {trend}
        </span>
      )}
    </div>
    {subValue && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{subValue}</p>}
  </div>
);

// Industry colors
const INDUSTRY_COLORS = {
  'Technology': { bg: 'bg-blue-100', text: 'text-blue-700' },
  'Finance': { bg: 'bg-purple-100', text: 'text-purple-700' },
  'Healthcare': { bg: 'bg-green-100', text: 'text-green-700' },
  'Manufacturing': { bg: 'bg-slate-100', text: 'text-slate-700' },
  'Retail': { bg: 'bg-pink-100', text: 'text-pink-700' },
  'Education': { bg: 'bg-amber-100', text: 'text-amber-700' },
  'Real Estate': { bg: 'bg-teal-100', text: 'text-teal-700' },
  'Telecom': { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  'Media': { bg: 'bg-orange-100', text: 'text-orange-700' },
  'default': { bg: 'bg-gray-100', text: 'text-gray-700' },
};

// Format currency in INR
const formatCurrency = (value) => {
  if (!value) return '₹0';
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);
};

function CompaniesPage({ embedded = false }) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const batchFilter = useMemo(() => searchParams.get('batch') || '', [searchParams]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [companyPage, setCompanyPage] = useState(1);
  const [companyLimit, setCompanyLimit] = useState(20);
  const [companyPagination, setCompanyPagination] = useState(null);
  const [companyStats, setCompanyStats] = useState(null);
  // Bulk-delete state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    employee_size: '',
    website: '',
    linkedin_url: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    phone: '',
    email: '',
    annual_revenue: '',
    description: '',
  });

  const INDUSTRIES = ['Technology', 'Finance', 'Healthcare', 'Manufacturing', 'Retail', 'Education', 'Real Estate', 'Telecom', 'Media', 'Other'];
  const EMPLOYEE_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

  const fetchCompanies = useCallback(async () => {
    try {
      const params = { page: companyPage, limit: companyLimit };
      if (searchTerm) params.search = searchTerm;
      if (industryFilter && industryFilter !== 'all') params.industry = industryFilter;
      if (batchFilter) {
        params.import_batch_id = batchFilter;
        // When viewing a batch, default to newest-first so the imported set
        // is surfaced at the top even if alphabetically scattered.
        params.sort = 'newest';
      }
      const response = await axios.get(`${API}/companies`, { params });
      if (response.data?.pagination) {
        setCompanies(response.data.data || []);
        setCompanyPagination(response.data.pagination);
      } else {
        setCompanies(response.data);
        setCompanyPagination(null);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, industryFilter, companyPage, companyLimit, batchFilter]);

  const fetchCompanyStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/companies/stats`);
      setCompanyStats(response.data);
    } catch (error) {
      console.error('Error fetching company stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies, location.state?.refresh]);

  useEffect(() => {
    fetchCompanyStats();
  }, [fetchCompanyStats, location.state?.refresh]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      setMessage({ type: 'error', text: 'Company name is required' });
      return;
    }

    try {
      // Trim empty strings → null so backend Pydantic HttpUrl/EmailStr validators pass
      const cleaned = Object.fromEntries(
        Object.entries(formData).map(([k, v]) => [k, typeof v === 'string' && v.trim() === '' ? null : v])
      );
      const submitData = {
        ...cleaned,
        annual_revenue: formData.annual_revenue ? parseFloat(formData.annual_revenue) : null,
      };
      await axios.post(`${API}/companies`, submitData);
      setMessage({ type: 'success', text: 'Company created successfully!' });
      setShowAddModal(false);
      resetForm();
      fetchCompanies();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : 'Failed to create company');
      setMessage({ type: 'error', text: msg });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', industry: '', employee_size: '', website: '', linkedin_url: '',
      address: '', city: '', state: '', country: 'India', pincode: '',
      phone: '', email: '', annual_revenue: '', description: '',
    });
  };

  // filteredCompanies uses server-side filtered data when paginated
  const filteredCompanies = companyPagination ? companies : companies.filter(company => {
    const matchesSearch = !searchTerm || 
      company.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.city?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesIndustry = industryFilter === 'all' || company.industry === industryFilter;
    return matchesSearch && matchesIndustry;
  });

  // Get unique industries from data
  const uniqueIndustries = [...new Set(companies.map(c => c.industry).filter(Boolean))];

  // KPI calculations — use server-side stats so counts reflect the FULL DB, not current page
  const totalCompaniesCount = companyStats?.total_companies ?? (companyPagination?.total ?? companies.length);
  const totalPipelineValue = companyStats?.total_pipeline ?? companies.reduce((sum, c) => sum + (c.total_deal_value || 0), 0);
  const totalStakeholders = companyStats?.total_stakeholders ?? companies.reduce((sum, c) => sum + (c.stakeholder_count || 0), 0);
  const avgStakeholders = companyStats?.avg_stakeholders ?? (companies.length > 0 ? (totalStakeholders / companies.length).toFixed(1) : 0);

  // Bulk-delete handlers
  const allVisibleIds = filteredCompanies.map(c => c.id);
  const allOnPageSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selectedIds.has(id));
  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allVisibleIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allVisibleIds.forEach(id => next.add(id));
        return next;
      });
    }
  };
  const toggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const confirmBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    try {
      setBulkDeleting(true);
      const ids = Array.from(selectedIds);
      const res = await axios.post(`${API}/companies/bulk-delete`, { ids });
      const n = res.data?.deleted ?? ids.length;
      setMessage({ type: 'success', text: `Deleted ${n} compan${n === 1 ? 'y' : 'ies'}` });
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      fetchCompanies();
      fetchCompanyStats();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Bulk delete failed';
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Bulk delete failed' });
    } finally {
      setBulkDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="companies-loading">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-5" : "min-h-screen bg-slate-50 dark:bg-slate-900 -m-4 lg:-m-6 p-4 lg:p-6 space-y-5"} data-testid="companies-page">
      {/* Header Section */}
      <div className={embedded ? "bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-4 lg:p-5 rounded-t-xl" : "bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 -mx-4 lg:-mx-6 -mt-4 lg:-mt-6 p-4 lg:p-6 mb-5"}>
        {/* Title Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{embedded ? 'Companies Directory' : 'Companies'}</h1>
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <button
            onClick={() => { resetForm(); setShowAddModal(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
            data-testid="add-company-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Company
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <KPICard title="Total Companies" value={totalCompaniesCount.toLocaleString()} trend={0} subValue="in database" />
          <KPICard title="Total Stakeholders" value={totalStakeholders} trend={0} subValue="contacts" />
          <KPICard title="Total Pipeline" value={formatCurrency(totalPipelineValue)} trend={0} subValue="deal value" />
          <KPICard title="Avg Stakeholders" value={avgStakeholders} trend={0} subValue="per company" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search companies..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCompanyPage(1); }}
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-900 dark:text-slate-100"
              data-testid="search-companies-input"
            />
          </div>
          <select
            value={industryFilter}
            onChange={(e) => { setIndustryFilter(e.target.value); setCompanyPage(1); }}
            className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-slate-400"
            data-testid="industry-filter"
          >
            <option value="all">All Industries</option>
            {uniqueIndustries.map(industry => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>
          {(searchTerm || industryFilter !== 'all') && (
            <button
              onClick={() => { setSearchTerm(''); setIndustryFilter('all'); }}
              className="px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Import-batch filter banner */}
        {batchFilter && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 flex items-center justify-between gap-3 text-xs text-purple-800 dark:text-purple-200" data-testid="companies-batch-banner">
            <div className="flex items-center gap-2">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
              <span>
                Filtered to companies from your recent import · <strong>{companyPagination?.total ?? companies.length}</strong> record{(companyPagination?.total ?? companies.length) === 1 ? '' : 's'}
              </span>
            </div>
            <button
              onClick={() => { setSearchParams({}); setCompanyPage(1); }}
              className="px-2 py-1 text-[11px] font-medium rounded bg-white dark:bg-slate-800 border border-purple-300 dark:border-purple-700 hover:bg-purple-100 dark:hover:bg-purple-900/40"
              data-testid="companies-clear-batch-filter"
            >
              Show all companies
            </button>
          </div>
        )}
      </div>

      {/* Message */}
      {message.text && (
        <div className={`px-4 py-3 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      {/* Companies Table */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700">
          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Companies List
            <InfoIcon />
          </span>
          <div className="flex items-center gap-3">
            {selectedIds.size > 0 && (
              <>
                <span className="text-xs text-slate-600 dark:text-slate-300" data-testid="bulk-selection-count">
                  {selectedIds.size} selected
                </span>
                <button
                  onClick={() => setBulkDeleteOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                  data-testid="bulk-delete-btn"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete {selectedIds.size} selected
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="px-2 py-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  data-testid="bulk-clear-btn"
                >
                  Clear
                </button>
              </>
            )}
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {companyPagination?.total ?? filteredCompanies.length} companies
            </span>
          </div>
        </div>
        
        {filteredCompanies.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">No Companies Found</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Start by adding your first company</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-slate-900 dark:bg-slate-100 dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-white rounded-lg transition-colors"
            >
              Add Company
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleSelectAll}
                      className="rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                      data-testid="select-all-companies"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Industry</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Stakeholders</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Pipeline</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {filteredCompanies.map((company) => {
                  const industryColor = INDUSTRY_COLORS[company.industry] || INDUSTRY_COLORS.default;
                  const isSelected = selectedIds.has(company.id);
                  return (
                    <tr
                      key={company.id}
                      className={`hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${isSelected ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''}`}
                      data-testid={`company-row-${company.id}`}
                    >
                      <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectOne(company.id)}
                          className="rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                          data-testid={`select-company-${company.id}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/companies/${company.id}`} className="flex items-center gap-3 group">
                          <CompanyLogo
                            domain={(company.website || '').replace(/^https?:\/\//, '').replace(/\/.*$/, '') || null}
                            name={company.name}
                            size={36}
                            className="flex-shrink-0"
                          />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate">{company.name}</div>
                            {company.website && (
                              <span className="text-xs text-slate-400 dark:text-slate-500 truncate block">
                                {company.website.replace(/https?:\/\//, '').replace(/\/$/, '')}
                              </span>
                            )}
                          </div>
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        {company.industry ? (
                          <span className={`text-xs font-medium px-2 py-1 rounded ${industryColor.bg} ${industryColor.text}`}>
                            {company.industry}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400 dark:text-slate-500">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-slate-700 dark:text-slate-300">{company.city || '-'}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{company.state || company.country || '-'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-slate-700 dark:text-slate-300 truncate max-w-[150px]">{company.email || '-'}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-500 dark:text-slate-400">{company.phone || '-'}</span>
                          {company.phone && (
                            <ClickToCall 
                              phone={company.phone} 
                              leadName={company.name}
                              variant="icon"
                              size="sm"
                            />
                          )}
                          {company.linkedin_url && (
                            <a
                              href={company.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-0.5 text-[#0A66C2] hover:bg-[#0A66C2]/10 rounded transition-colors"
                            >
                              <Linkedin className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{company.stakeholder_count || 0}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">contacts</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{formatCurrency(company.total_deal_value)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Link
                            to={`/companies/${company.id}`}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded transition-colors inline-block"
                            title="View Details"
                            data-testid={`view-company-${company.id}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </Link>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedIds(new Set([company.id]));
                              setBulkDeleteOpen(true);
                            }}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                            title="Delete"
                            data-testid={`delete-company-${company.id}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {companyPagination && (
          <Pagination
            pagination={companyPagination}
            onPageChange={(p) => setCompanyPage(p)}
            onLimitChange={(l) => { setCompanyLimit(l); setCompanyPage(1); }}
          />
        )}
      </div>

      {/* Add Company Modal */}
      {showAddModal && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={() => setShowAddModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="company-modal">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Add New Company</h3>
                <button 
                  onClick={() => setShowAddModal(false)}
                  className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Company Name */}
              <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide block mb-2">Company Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  placeholder="Acme Corporation"
                  data-testid="company-name-input"
                />
              </div>

              {/* Basic Info */}
              <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-3">
                <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Basic Info</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 dark:text-slate-500 block mb-1">Industry</label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    >
                      <option value="">Select</option>
                      {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 dark:text-slate-500 block mb-1">Employee Size</label>
                    <select
                      name="employee_size"
                      value={formData.employee_size}
                      onChange={(e) => setFormData({ ...formData, employee_size: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    >
                      <option value="">Select</option>
                      {EMPLOYEE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 dark:text-slate-500 block mb-1">Website</label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                      placeholder="https://company.com"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 dark:text-slate-500 block mb-1">LinkedIn URL</label>
                    <input
                      type="url"
                      name="linkedin_url"
                      value={formData.linkedin_url}
                      onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                      placeholder="https://linkedin.com/company/..."
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-3">
                <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Contact Info</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 dark:text-slate-500 block mb-1">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                      placeholder="+91-9876543210"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 dark:text-slate-500 block mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                      placeholder="contact@company.com"
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-3">
                <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Location</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 dark:text-slate-500 block mb-1">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                      placeholder="Mumbai"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 dark:text-slate-500 block mb-1">State</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                      placeholder="Maharashtra"
                    />
                  </div>
                </div>
              </div>

              {/* Revenue */}
              <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide block mb-2">Annual Revenue (INR)</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-slate-500 dark:text-slate-400">₹</span>
                  <input
                    type="number"
                    name="annual_revenue"
                    value={formData.annual_revenue}
                    onChange={(e) => setFormData({ ...formData, annual_revenue: e.target.value })}
                    className="w-full pl-8 pr-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    placeholder="10000000"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide block mb-2">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  placeholder="Brief description about the company..."
                />
              </div>
            </form>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-slate-900 dark:bg-blue-600 hover:bg-slate-800 dark:hover:bg-slate-800 dark:hover:bg-white rounded-lg transition-colors"
                data-testid="save-company-btn"
              >
                Save Company
              </button>
            </div>
          </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>

      {/* Bulk Delete Confirmation */}
      {bulkDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="bulk-delete-modal">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Delete {selectedIds.size} compan{selectedIds.size === 1 ? 'y' : 'ies'}?</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
              This permanently removes the selected compan{selectedIds.size === 1 ? 'y' : 'ies'} and all linked stakeholders. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setBulkDeleteOpen(false)}
                disabled={bulkDeleting}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                data-testid="bulk-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 rounded-lg"
                data-testid="bulk-delete-confirm"
              >
                {bulkDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompaniesPage;
